import { motion, useReducedMotion } from "framer-motion";
import type { IntentItem } from "../mockAnalytics";

interface TopIntentsListProps {
  data: IntentItem[];
}

/** Ranked list of the most common customer questions/intents, with relative bars. */
export function TopIntentsList({ data }: TopIntentsListProps) {
  const prefersReducedMotion = useReducedMotion();
  const max = data[0]?.count || 1;

  if (!data || data.length === 0) {
    return (
      <div className="flex h-44 flex-col items-center justify-center text-center">
        <p className="text-xs text-slate-500 dark:text-slate-400">No customer intents recorded yet.</p>
        <span className="mt-1 text-[11px] text-slate-400 dark:text-slate-500">
          Inquiries will appear here as conversations take place.
        </span>
      </div>
    );
  }

  return (
    <ul className="space-y-3">
      {data.map((intent, i) => {
        const widthPct = Math.max(8, (intent.count / max) * 100);
        return (
          <li key={intent.label} className="flex items-center gap-3">
            <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-md bg-slate-100 dark:bg-slate-800 text-[11px] font-bold text-slate-600 dark:text-slate-400">
              {i + 1}
            </span>
            <div className="min-w-0 flex-1">
              <div className="mb-1.5 flex items-center justify-between gap-2">
                <span className="truncate text-xs font-semibold text-slate-800 dark:text-slate-200">
                  {intent.label}
                </span>
                <span className="shrink-0 text-xs font-bold tabular-nums text-slate-900 dark:text-white">
                  {intent.count.toLocaleString()}
                </span>
              </div>
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
                <motion.div
                  className="h-full rounded-full bg-emerald-500"
                  initial={{ width: prefersReducedMotion ? `${widthPct}%` : 0 }}
                  animate={{ width: `${widthPct}%` }}
                  transition={{ duration: 0.6, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] }}
                />
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
}
